import { mapGetters } from "vuex";

import actionBarComponent from "@/components/child/actionBar/actionBar.vue";
import cepFieldComponent from "@/components/child/form/cepfield.vue";
import checkboxComponent from "@/components/child/form/checkbox.vue";
import cnpjComponent from "@/components/child/form/cnpj.vue";
import comboComponent from "@/components/child/form/combo.vue";
import cpfComponent from "@/components/child/form/cpf.vue";
import dataTooltipComponent from "@/components/child/form/datatooltip.vue";
import dateTimePickerComponent from "@/components/child/form/datetimepicker.vue";
import fieldsetComponent from "@/components/child/form/fieldset.vue";
import infoBagdeComponent from "@/components/child/infoBagde/infoBagde.vue";
import logEntidadeComponent from "@/components/child/logEntidade/logEntidade.vue";
import { Component, Prop, Watch } from "@/decorators";
import CepModel from "@/models/cepModel";
import EnumExtensions from "@/models/enum/extensions/enumExtensions";
import TipoConta from "@/models/enum/tipoConta";
import TipoMovimentacao from "@/models/enum/tiposMovimentacao";
import TiposPessoa from "@/models/enum/tiposPessoa";
import Ufs from "@/models/enum/ufs";
import ContaGerencialModel from "@/models/financeiro/contaGerencialModel";
import FornecedorModel from "@/models/fornecedorModel";
import PaginationModel from "@/models/paginationModel";
import CidadeService from "@/services/external/cidadeService";
import ContaGerencialService from "@/services/financeiro/contaGerencialService";
import FornecedorService from "@/services/fornecedorService";
import PaisService from "@/services/paisService";
import { editDateWithTime } from "@/utils/common/date";
import { AcaoController, DescricaoTela } from "@/utils/common/permissionConstants";

import { Getters, SessionActions } from "../../../../store/store";
import EditComponentBase from "../editComponentBase";

import "../crud.scss";

@Component({
    components: {
        comboComponent,
        checkboxComponent,
        fieldsetComponent,
        cpfComponent,
        cnpjComponent,
        logEntidadeComponent,
        cepFieldComponent,
        dataTooltipComponent,
        actionBarComponent,
        infoBagdeComponent,
        dateTimePickerComponent,
    },
    computed: {
        ...mapGetters(["GET_FRANQUIA_IS_ESTEREIS", "VALIDAR_PERMISSAO_USUARIO"] as Getters),
        infoBagdeText() {
            return "Data criação: " + editDateWithTime(this.model.dataCriacao);
        },
    },
})
export default class FornecedorEditComponent extends EditComponentBase<FornecedorModel> {
    // State computed props
    GET_FRANQUIA_IS_ESTEREIS: () => Promise<boolean>;
    VALIDAR_PERMISSAO_USUARIO: (telaDescricao: string, acaoController: string) => boolean;
    infoBagdeText: string;

    private cidadeService = new CidadeService();
    private isGrupoAdmin = false;
    private isEstereis = false;
    private obrigaCamposSenior = false;
    tiposPessoa = TiposPessoa;
    tiposPessoaOptions: Array<Object> = [
        { text: "F\u00edsica", value: TiposPessoa.Fisica },
        { text: "Jur\u00eddica", value: TiposPessoa.Juridica },
    ];

    dadosCep = new CepModel();
    ufOptions: Array<Object> = EnumExtensions.getNamesAndValuesOrderedByNames(Ufs);
    cidadesOptions: Array<Object> = [];
    paisesOptions: Array<Object> = [];
    contasGerenciaisOptions: Array<Object> = [];
    tipoContaOptions: Array<Object> = Object.entries(TipoConta)
        .filter(([key, value]) => typeof value === "string")
        .map(([key, value]) => ({
            text: `${value} - ${key.replace(/([A-Z])/g, " $1").trim()}`,
            value: value,
        }))
        .sort((a, b) => a.value.localeCompare(b.value));
    tipoChavePixOptions: Array<Object> = [
        { value: "1", text: "1 - Telefone" },
        { value: "2", text: "2 - Email" },
        { value: "3", text: "3 - CPF/CNPJ" },
        { value: "4", text: "4 - Chave Aleatória" },
    ];
    tipoEmpresaOptions: Array<Object> = [
        { value: 1, text: "1 - Indústria" },
        { value: 2, text: "2 - Comércio" },
        { value: 3, text: "3 - Distribuidor" },
        { value: 4, text: "4 - TRR (Transp./Rev./Retalhista)" },
        { value: 5, text: "5 - Extração Mineral/Outros" },
        { value: 6, text: "6 - Indústria/Transporte" },
        { value: 7, text: "7 - Produtor Rural" },
        { value: 8, text: "8 - Prestador de Serviço" },
        { value: 9, text: "9 - Atividade Financeira" },
        { value: 10, text: "10 - Atividade Imobiliária" },
        { value: 11, text: "11 - Cooperativa" },
        {
            value: 12,
            text: "12 - Sociedades Seguradoras, de Capitalização ou Entidade Aberta de Previdência Complementar",
        },
        { value: 13, text: "13 - Distribuidor de Energia Elétrica" },
        { value: 14, text: "14 - Prestador de Serviço de Comunicação" },
        { value: 99, text: "99 - Outros" },
    ];
    tipoFornecedorOptions: Array<Object> = [
        { value: "M", text: "M - Matéria Prima" },
        { value: "P", text: "P - Prestador de Serviços" },
        { value: "A", text: "A - Ambos" },
    ];

    indicativoRegistroOptions: Array<Object> = [
        { value: "C", text: "Cliente" },
        { value: "F", text: "Fornecedor" },
        { value: "A", text: "Ambos" },
    ];

    codigoRegimeTributarioOptions: Array<Object> = [
        { value: "0", text: "Todos" },
        { value: "1", text: "Simples Nacional" },
        { value: "2", text: "Simples Nascional excesso de sublimite de receita bruta" },
        { value: "3", text: "Regime Normal" },
        { value: "4", text: "Simples Nascional / MEI" },
    ];

    tipoPropriedadeEmpresaOptions: Array<Object> = [
        { value: "1", text: "Privada" },
        { value: "2", text: "Pública" },
    ];

    private bloqueiaEdicaoSenior = false;

    @Prop({ type: Boolean, default: false }) CalledByShortCut: boolean = false;

    private async beforeMount() {
        this.isModal = this.CalledByShortCut;

        await this.$store.dispatch(SessionActions.LOAD_USUARIO_LOGADO);
        this.isEstereis = await this.GET_FRANQUIA_IS_ESTEREIS();

        const ambiente = this.$store.state.session.usuarioLogado.ambiente;
        this.obrigaCamposSenior = ambiente === "Development" || ambiente === "Staging";

        const novoModel = new FornecedorModel();
        if (this.isEstereis) {
            this.applyDefaultsSenior(novoModel);
        }

        await this.setProps(new FornecedorService(), "fornecedores", novoModel, this.CalledByShortCut);
        this.isGrupoAdmin = await this.HAS_PERMISSAO_PERFIL(false, false, false);
        this.loadPaises();

        const isAdmin = await this.HAS_PERMISSAO_PERFIL(false, false, false);
        const temPermissao = this.VALIDAR_PERMISSAO_USUARIO(DescricaoTela.fornecedores, AcaoController.seniorUpdate);
        this.bloqueiaEdicaoSenior = !isAdmin && !temPermissao;

        Promise.all([this.loadContasGerenciais()]).withLoading();
    }

    public async onLoadModel(model: FornecedorModel) {
        await this.onLoadCidades(model.estadoId);
    }

    public async onPreSave() {
        if (this.model.cidadeId) {
            const cidade = this.cidadesOptions.find(item => item["value"] == this.model.cidadeId);
            if (cidade) {
                this.model.cidade = cidade["text"];
            }
        }

        if (this.model.codigoPais != null) {
            this.model.pais =
                this.paisesOptions.find(item => item["value"] == this.model.codigoPais)?.["text"] ?? this.model.pais;
        }

        return true;
    }

    private async onCepChanged(data: CepModel) {
        this.dadosCep = data;
        this.model.bairro = data.bairro;
        this.model.complemento = data.complemento;
        this.model.endereco = data.endereco;
        this.model.estadoId = data.uf;

        await this.onLoadCidades(this.model.estadoId);
        this.model.cidadeId = data.cidadeId;
    }

    private onCepInvalid() {
        this.$showInfo(this.$t("__.ts.pesqCEP"), this.$t("__.ts.pesqNaoEncResul"));
    }

    private async onShowLogsFornecedor(open: (slot: number, title: string, size: string) => {}) {
        open(0, this.$t("__.ts.historicoAlteracoes") as string, "lg-modal");
    }

    //@ts-ignore
    @Watch("model.tipoPessoa")
    private onTipoPessoaChanged(tipoPessoa: TiposPessoa) {
        switch (tipoPessoa) {
            case TiposPessoa.Fisica:
                this.model.nomeFantasia = null;
                this.model.cnpj = null;
                this.model.inscricaoEstadual = null;
                this.model.razaoSocial = null;
                break;
            case TiposPessoa.Juridica:
                this.model.nome = null;
                this.model.cpf = null;
                this.model.rg = null;
                break;
        }
    }

    private async onLoadCidades(estadoId: number) {
        try {
            this.cidadesOptions = [];
            if (estadoId) {
                this.cidadesOptions = await this.cidadeService.get(estadoId).withLoading();
            }
        } catch {}
    }

    private async loadContasGerenciais() {
        try {
            const data = await new ContaGerencialService()
                .listByTipoMovimentacao("", 1, 999999, TipoMovimentacao.Saida)
                .resolveWithJSON<PaginationModel<ContaGerencialModel>>();
            this.contasGerenciaisOptions = data.list.map(p => ({ value: p.id, text: p.descricao }));
        } catch {}
    }

    private loadPaises() {
        const paises = new PaisService().get();
        this.paisesOptions = paises.map(pais => ({ value: pais.codigoSPED, text: pais.descricao }));
    }

    private applyDefaultsSenior(model: FornecedorModel) {
        model.notaFiscaisRetencaoCofins = true;
        model.notaFiscaisRetencaoCSLL = true;
        model.notaFiscaisRetencaoPIS = true;
        model.notaFiscaisOutrasRetencoes = true;
        model.notaFiscaisRecuperacaoIPI = true;
        model.notaFiscaisRecuperacaoICMS = true;
        model.fornecedorTributacaoICMS = true;
        model.fornecedorTributacaoIPI = true;
        model.fornecedorControlaRetencoes = true;
    }
}

